<template>
  <div>
    <div class="container" v-show="!isBusy || !busyMessage">
      <div class="arrow-back">
        <!-- javascipt:history.back because we are not using vue router -->
        <a href="javascript:history.back()">
          <img class="mr-1 clickable" :src="backIcon" @click="$emit('back')" />
        </a>
      </div>
      <div class="icon-circle">
        <a-icon icon="24:lock-locked" width="32" heigh="32"></a-icon>
      </div>
      <h1>{{ $t('otp.enterCode') }}</h1>

      <div class="otp-container" v-bind:class="{ disabled: isBusy }">
        <p>
          {{
            $t(`otp.codeDetail.${selectedContactMethod.name}`, {
              login: userLogin,
            })
          }}
          <template v-if="expires">
            {{ $t('otp.enterCodeExpires', { expires }) }}
          </template>
        </p>
        <v-otp-input
          ref="otpInput"
          input-classes="otp-input"
          inputmode="numeric"
          separator=""
          :num-inputs="6"
          @on-change="handleOnChange"
          @on-complete="handleOnChange"
        />
        <a-button
          type="button"
          :disabled="code.length != 6"
          @click="confirmCode"
          style="width: 100%; border-radius: 8px;"
        >
          {{ $t('confirm') }}
        </a-button>
      </div>
      <p class="otp-error" v-if="errorMessage">{{ errorMessage }}</p>

      <p class="resend" :class="{ disabled: seconds > 0 }">
        <a
          class="purple-link clickable"
          @click="requestNewOtp(selectedContactMethod)"
        >
          {{ $t('otp.resendCode') }}
        </a>
        <span v-if="seconds > 0">
          (00:{{ seconds.toString().padStart(2, '0') }})
        </span>
      </p>
    </div>
    <div class="busy-container" v-show="isBusy && busyMessage">
      <h1>{{ busyMessage }}</h1>
    </div>
  </div>
</template>

<script>
import {
  LoginFormClient,
  OtpRestClient,
  OtpService,
} from '../services/OtpService';

export default {
  name: 'OtpForm',

  beforeMount() {
    this.otpService = new OtpService();
    this.primaryContactMethods = JSON.parse(this.contacts);
    this.selectedContactMethod = this.preferredContactMethod;
  },
  beforeUnmount() {
    clearInterval(this.interval);
  },
  mounted() {
    this.userLogin = document.getElementById('user_login')?.value;
  },
  data() {
    return {
      userLogin: '',
      isBusy: false,
      primaryContactMethods: [],
      selectedContactMethod: null,
      busyMessage: null,
      errorMessage:
        this.message && this.message.trim() !== '' ? this.message : null,
      code: '',
      seconds: 0,
      countDownInterval: null,
    };
  },
  props: {
    expires: {
      // This is only for display purposes. It's not used in the code.
      type: String,
      required: false,
      default: null,
    },
    user: {
      type: String,
      required: true,
    },
    contacts: {
      type: String,
      required: true,
    },
    // Since we are still submitting via HTML form, if a user sumbits an invalid OTP value,
    // the form is submitted and page is refreshed with an error message. This is how we expose that message
    // until we can submit via REST and handle errors in the component internally.
    message: {
      type: String,
      required: false,
    },
  },
  computed: {
    preferredContactMethod() {
      const preferredMethod = this.primaryContactMethods.find(
        (m) => m.preferred === true
      );
      return preferredMethod ? preferredMethod : this.primaryContactMethods[0];
    },
    otherContactMethods() {
      return this.primaryContactMethods.filter(
        (m) => m.name !== this.selectedContactMethod.name
      );
    },
    backIcon() {
      return new URL(
        '../../assets/images/icons/arrow-left.svg',
        import.meta.url
      ).href;
    },
  },
  methods: {
    setBusy(message) {
      this.isBusy = true;
      this.busyMessage = message;
      this.errorMessage = null;
    },
    clearBusy() {
      this.isBusy = false;
      this.busyMessage = null;
    },
    handleOnChange(value) {
      this.code = value.toString();
    },
    confirmCode() {
      this.setBusy(this.$t('otp.verifying'));
      this.otpService
        .usingClient(new LoginFormClient())
        .verify(this.user, this.selectedContactMethod.name, this.code)
        .catch((error) => {
          console.error(error);
          this.$notify.error(this.$t('otp.invalidCode'));
          this.$refs.otpInput.clearInput();
        })
        .finally(() => {
          this.clearBusy();
        });
    },
    setSelectedContactMethod(contactMethod) {
      this.selectedContactMethod = contactMethod;
    },
    requestNewOtp(contactMethod) {
      if (this.seconds > 0) return;

      this.setBusy();
      this.setSelectedContactMethod(contactMethod);
      this.otpService
        .usingClient(new OtpRestClient(this.$clients.oauth))
        .requestNewOtp(this.user, this.selectedContactMethod.name)
        .then((response) => {
          this.$notify({
            group: 'default',
            type: 'info',
            duration: 2000,
            title: this.$t(
              'otp.newCodeSent.' + this.selectedContactMethod.name,
              { masked: this.selectedContactMethod.masked }
            ),
          });
        })
        .catch((error) => {
          this.$notify({
            type: 'error',
            duration: 2000,
            title: this.$t(
              'otp.errorSendingCode.' + this.selectedContactMethod.name,
              { masked: this.selectedContactMethod.masked }
            ),
            text:
              this.$t('otp.codeDetail.' + this.selectedContactMethod.name, {
                masked: this.selectedContactMethod.masked,
              }) +
              ' ' +
              this.$t('otp.enterCodeExpires', { expires: this.expires }),
          });
        })
        .finally(() => {
          this.startCountdown();
          this.clearBusy();
          this.$refs.otpInput.clearInput();
        });
    },
    startCountdown() {
      this.seconds = 59;
      this.interval = setInterval(() => {
        if (this.seconds === 0) {
          clearInterval(this.interval);
        } else {
          this.seconds = this.seconds - 1;
        }
      }, 1000);
    },
  },
};
</script>

<style lang="scss">
@import '../../assets/stylesheets/resources/vars/colors';

.container {
  max-width: 400px;
}

.arrow-back {
  text-align: left;
}

.icon-circle {
  position: relative;
  color: var(--dscl-primary, var(--purple));
  height: 72px;
  width: 72px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 24px auto;
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: var(--dscl-primary, var(--purple));
    opacity: 0.1;
    border-radius: 100%;
  }
}
.disabled {
  transition: all 0.1s ease-out;
  opacity: 0.3;
  .purple-link {
    color: $color-dark !important;
  }
}
.resend {
  margin-top: 16px;
  a {
    cursor: pointer;
  }
}
.otp-error {
  margin-top: 16px;
  margin-bottom: 16px;
  color: red;
}
.otp-container {
  margin-top: 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 40px;
  width: 100%;
  margin: 32px auto 8px;
  > .a-button {
    justify-content: center;
    height: 48px;
  }
}
.otp-input-container {
  gap: 4px;
  display: flex;
  max-width: 400px;
  > div:nth-child(3)::after {
    content: ' ';
    background-color: var(--grey-30);
    width: 16px;
    height: 1px;
    margin-left: 8px;
    margin-right: 4px;
  }
  .otp-input {
    display: flex;
    width: 48px;
    height: 60px;
    padding: 5px;
    font-size: 20px;
    border-radius: 4px;
    border: 1px solid var(--grey-30);
    text-align: center;
    &:focus {
      outline: none;
      border: 1px solid var(--brand-1);
    }
    &.error {
      border: 1px solid red !important;
    }
  }
}
.otp-input::-webkit-inner-spin-button,
.otp-input::-webkit-outer-spin-button {
  -webkit-appearance: none;
  margin: 0;
}
input[type='number'].otp-input {
  -moz-appearance: textfield;
}

@media (max-width: 600px) {
  .otp-input-container {
    gap: 4px;
    display: flex;
    max-width: 400px;
    > div:nth-child(3)::after {
      content: ' ';
      background-color: var(--grey-30);
      width: 8px;
    }
    .otp-input {
      display: flex;
      width: 38px;
      height: 50px;
      padding: 5px;
      font-size: 20px;
      border-radius: 4px;
      border: 1px solid var(--grey-30);
      text-align: center;
    }
  }
}
</style>
